/**
 * Reading this browser, for `reach.ts` to decide from. F-141.
 *
 * The DOM half. `reach.ts` knows nothing about a browser and decides everything; this knows about a
 * browser and decides nothing. The split is `install/route.ts` and `install/prompt.ts`'s, for the
 * reason that file gives: the last platform-detection decision in this codebase to live inside a
 * component shipped four wrong fixes before anybody measured anything.
 *
 * **Nothing here is read at module load.** `installFacts()` touches `matchMedia` and
 * `navigator.standalone`, and this module is imported by a component that is rendered on a route —
 * so a read at import time would run before the service worker has registered on a cold start and
 * would answer about a browser that had not finished starting.
 */
import { installFacts } from '../install/prompt.js';
import { installRoute, isSafari } from '../install/route.js';
import { permission, subscribedHere, support } from '../push.js';
import { getPushState, isError } from '../api.js';
import type { ReachFacts } from './reach.js';

/**
 * A device the learner carries, as a capability rather than a name.
 *
 * `(pointer: coarse)` is the query for "the primary input is a finger", which is the actual thing
 * being asked: it is true of phones and tablets and false of laptops, including the touchscreen
 * ones, whose primary pointer is still a trackpad. Deliberately not a user-agent test — the list of
 * phone names is endless, wrong for every new device, and this question has a feature to ask.
 *
 * An iPad reporting itself as a Mac is caught by the Apple check rather than by the media query,
 * which is the one case where the capability answer alone would be wrong for the copy: an iPad is
 * offered the home screen and a Mac is not.
 */
function handheld(apple: boolean): boolean {
  if (apple) return true;
  try {
    return window.matchMedia('(pointer: coarse)').matches;
  } catch {
    // A browser with no `matchMedia` is old enough that the desktop answer is the likelier one, and
    // the cost of being wrong is a QR code on a phone instead of an install card.
    return false;
  }
}

/**
 * Safari on a Mac, which is neither iOS nor an ordinary desktop.
 *
 * `installFacts().apple` is false here by construction — it requires touch points — so this is the
 * case that falls between the two. Recent macOS Safari raises web notifications only for a site
 * added to the Dock and older ones cannot at all, and both want a sentence naming a menu item a
 * Mac actually has. Without this the same browser is told "this browser cannot do it", which is
 * true of the tab and false of the machine.
 */
function isDesktopSafari(apple: boolean): boolean {
  if (apple) return false;
  return isSafari(navigator.userAgent) && /Mac/i.test(navigator.platform || navigator.userAgent);
}

/**
 * Everything `reachPlan` needs, read from this browser and this account.
 *
 * **`subscribed` is both halves and not either one.** The browser's own
 * `pushManager.getSubscription()` says this device holds a subscription; the API says how many
 * devices the account has rows for. A browser that says yes over a server that says none is a real,
 * reachable state — the service forgets a device the moment a push endpoint answers 404 or 410, and
 * the browser is never told — and in it the learner believes notifications work and receives
 * nothing. Requiring both is what stops the first run ending on that belief.
 *
 * A failed read of the push state counts as **not** subscribed, which leaves the learner looking at
 * the control and reading that email will carry the questions. That is the safe direction: the
 * other one ends the first run by telling somebody they are reachable on the strength of a request
 * that did not come back.
 */
export async function reachFacts(): Promise<ReachFacts> {
  const facts = installFacts();
  const available = support();

  let devices = 0;
  const state = await getPushState();
  if (!isError(state)) devices = state.devices;

  return {
    install: installRoute(facts),
    support: available,
    permission: permission(),
    subscribed: devices > 0 && (await subscribedHere()),
    handheld: handheld(facts.apple),
    desktopSafari: isDesktopSafari(facts.apple),
  };
}
